/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// useCompletionChecklist
//
// Standalone sibling of useGuidedChecklist, used by the COMPLETED-TREATMENT
// wrap-up hub (CompletedTreatmentHub). It loads the WHOLE of the lead's current
// treatment checklist — every open item across every stage — so the user can
// wrap up the entire treatment before closing it.
//
// SCOPE (relaxed 2026-05-28 per user feedback): the hub no longer narrows items
// by their template's `applies_to_statuses` stage. A multi-stage treatment
// checklist would previously collapse to the single item tagged TREATMENT_STARTED;
// now ALL open items for the lead's current treatment are shown, regardless of
// the stage they are tagged for. It still does NOT build a post-completion
// (review / recall) set — that is a future phase.
//
// WHAT THE FILTER NOW DOES:
//   - KEEPS treatment-specific scoping: only items belonging to the lead's
//     current treatment (matched via treatment_item.id + the lead_treatment row)
//     are shown — other treatments' items are excluded.
//   - KEEPS the tasks-v2 feature gate (early empty return, no API when disabled).
//   - KEEPS the active/done bucketing (today / later / done).
//   - DROPS the per-stage status narrowing (applies_to_statuses is no longer a
//     filter — items from any stage of this treatment's checklist all appear).
//
// Why a standalone hook (not a param on useGuidedChecklist):
//   useGuidedChecklist powers the live attended popup and MUST NOT regress.
//   We reuse its already-exported `filterGroupsByTreatment` helper (and accept
//   every stage rather than just one).
//
// Differences from useGuidedChecklist:
//   - No per-stage status narrowing (shows the whole treatment checklist)
//   - No spawn-retry loop (tasks already exist by the time treatment completes)
//
// Same return shape:
//   { loading, initialLoading, templatesEmpty, groups, upNext,
//     templatePreviewItems, allDoneCount, refresh }
//
// Data sources (identical to useGuidedChecklist):
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → spawned tasks
//   2. /api/v1/practices/{p}/task-templates/?limit=200   → template metadata
// -----------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { filterGroupsByTreatment } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';

// The hub's "current treatment" status. Used only to pick which lead_treatment
// row is the active one (NOT to narrow checklist items by stage — see header).
const ACTIVE_TREATMENT_STATUS = 'TREATMENT_STARTED';

// ---------------------------------------------------------------------------
// Helpers (mirror useGuidedChecklist)
// ---------------------------------------------------------------------------

/** Midnight at the start of today (local). */
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

/** One millisecond before midnight at the end of today (local). */
function endOfToday() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
}

/** True when the checklist item is completed, skipped, or paused — all out of the active queue. */
function isItemCompleted(item) {
    if (!item) return false;
    if (item.completed_at != null) return true;
    if (item.status === 'completed') return true;
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED) return true;
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) return true;
    return false;
}

// ---------------------------------------------------------------------------
// Flat-item builder
// ---------------------------------------------------------------------------

// NOTE: no per-stage status narrowing here — every group that survived the
// treatment scoping (filterGroupsByTreatment) contributes its items.
function buildFlatItems(groups) {
    const items = [];
    let globalOrder = 0;

    groups.forEach((group) => {
        const tasks = group.tasks || [];
        tasks.forEach((task) => {
            const checklistItems = task.checklist_items || [];
            if (checklistItems.length === 0) return;

            const sortedItems = [...checklistItems].sort(
                (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
            );

            sortedItems.forEach((item) => {
                const resolvedType = item.item_type || item.action_type || '';
                const resolvedTitle = item.title || item.name || '';
                if (!resolvedType && !resolvedTitle) return;

                items.push({
                    itemId: item.id ?? item.checklist_item_id,
                    id: item.id ?? item.checklist_item_id,
                    taskId: task.id,
                    taskTitle: task.title,
                    templateId: group.template_id,
                    templateName: group.template_name,
                    item_type: item.item_type || item.action_type || '',
                    title: item.title || item.name || '',
                    description: item.description || '',
                    completed_at: item.completed_at || null,
                    status: item.status || null,
                    sort_order: item.sort_order ?? null,
                    due_at: task.due_at || null,
                    completion_state: item.completion_state || null,
                    is_completed: item.is_completed ?? null,
                    paused_until: item.paused_until || null,
                    done: isItemCompleted(item),
                    templateOrder: globalOrder,
                    raw: item,
                    rawTask: task,
                });
                globalOrder += 1;
            });
        });
    });

    return items;
}

// ---------------------------------------------------------------------------
// Group builder
// ---------------------------------------------------------------------------

function buildGroups(flatItems) {
    const todayEnd = endOfToday();

    const today = [];
    const later = [];
    const done = [];

    flatItems.forEach((item) => {
        if (item.done) {
            done.push(item);
            return;
        }
        if (!item.due_at) {
            later.push(item);
            return;
        }
        const d = new Date(item.due_at);
        if (d <= todayEnd) {
            today.push(item);
        } else {
            later.push(item);
        }
    });

    today.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));

    return { today, later, done };
}

// ---------------------------------------------------------------------------
// Up Next selector
// ---------------------------------------------------------------------------

function selectUpNext(flatItems) {
    const todayStart = startOfToday();
    const todayEnd = endOfToday();
    const incomplete = flatItems.filter((i) => !i.done);
    if (incomplete.length === 0) return null;

    const overdue = incomplete
        .filter((i) => i.due_at && new Date(i.due_at) < todayStart)
        .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    if (overdue.length > 0) return overdue[0];

    const dueToday = incomplete
        .filter((i) => {
            if (!i.due_at) return false;
            const d = new Date(i.due_at);
            return d >= todayStart && d <= todayEnd;
        })
        .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    if (dueToday.length > 0) return dueToday[0];

    return incomplete.sort((a, b) => a.templateOrder - b.templateOrder)[0];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} params
 * @param {string|number} params.practiceId
 * @param {string|number} params.leadId
 * @param {object}        params.leadDetail  – reconstructed lead object (carries
 *                        lead_treatments[] so the treatment filter resolves).
 *
 * @returns {{
 *   loading: boolean,
 *   initialLoading: boolean,
 *   templatesEmpty: boolean,
 *   groups: { today: object[], later: object[], done: object[] },
 *   upNext: object|null,
 *   templatePreviewItems: object[],
 *   allDoneCount: number,
 *   refresh: function,
 * }}
 */
export default function useCompletionChecklist({ practiceId, leadId, leadDetail }) {
    const [loading, setLoading] = useState(true);
    const hasDataRef = useRef(false);
    const [templatesEmpty, setTemplatesEmpty] = useState(false);
    const [groups, setGroups] = useState({ today: [], later: [], done: [] });
    const [upNext, setUpNext] = useState(null);
    const [templatePreviewItems, setTemplatePreviewItems] = useState([]);
    const [allDoneCount, setAllDoneCount] = useState(0);

    const templatesUpdatedAt = useSelector((s) => s?.taskTemplate?.templatesUpdatedAt || 0);

    const fetchAll = useCallback(async ({ bustCache = false } = {}) => {
        if (!practiceId || !leadId) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        // Feature-gate: never fire API calls for practices without tasks-v2.
        // MUST early-return empty (no API calls) — mirrors useGuidedChecklist.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        setLoading(true);
        try {
            const [checklistsRes, tplRes] = await Promise.all([
                callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`,
                    'GET', null, false,
                    bustCache ? { bustCache: true } : {}
                ),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);

            let groupsData = [];
            if (Array.isArray(checklistsRes?.data)) groupsData = checklistsRes.data;
            else if (Array.isArray(checklistsRes?.data?.results)) groupsData = checklistsRes.data.results;

            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;

            // Resolve the lead's active treatment id for the treatment-specific
            // filter. Priority: TREATMENT_STARTED → first treatment.
            // treatment_data.treatment_item.id is the master TreatmentItem ID,
            // which matches applies_to_treatment_items[].id on templates.
            const allTreatments = leadDetail?.lead_treatments || [];
            const matchedTreatment =
                allTreatments.find((t) => t.status === ACTIVE_TREATMENT_STATUS) ||
                allTreatments[0];
            const leadTreatmentId = matchedTreatment?.treatment_data?.treatment_item?.id ?? null;
            const matchedLeadTreatmentId = matchedTreatment?.id ?? null;
            if (matchedTreatment && leadTreatmentId == null) {
                Sentry.captureMessage('useCompletionChecklist: leadTreatmentItemId missing on lead_treatment', {
                    level: 'warning',
                    tags: { context: 'completion_checklist_treatment_id' },
                    extra: { practiceId, leadId, treatmentDataKeys: Object.keys(matchedTreatment?.treatment_data || {}) },
                });
            }

            const passesTreatmentFilter = (t) => {
                const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                if (tItems.length === 0) return true; // generic
                if (leadTreatmentId == null) return false;
                return tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
            };

            // Relaxed: no longer require a template that applies to TREATMENT_STARTED.
            // Any active, treatment-matching template with items qualifies, so the
            // hub surfaces the whole treatment checklist across every stage.
            const applicableTemplatesExist = tplData.some((t) => {
                const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                const isActive = t.is_active !== false;
                return items.length > 0 && isActive && passesTreatmentFilter(t);
            });

            if (!applicableTemplatesExist) {
                setTemplatesEmpty(true);
                setGroups({ today: [], later: [], done: [] });
                setUpNext(null);
                setTemplatePreviewItems([]);
                setAllDoneCount(0);
                return;
            }

            const tplTreatmentMap = new Map();
            tplData.forEach((t) => {
                tplTreatmentMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []
                );
            });

            // Reuse the exported helper from useGuidedChecklist for its treatment +
            // lead_treatment-row scoping. NOTE: that helper hard-codes its template
            // status gate to CONSULTATION_ATTENDED. Because the hub wants EVERY stage
            // (no per-stage narrowing), we feed it a status map that reports
            // CONSULTATION_ATTENDED for ALL templates — so the status gate is a no-op
            // and only the treatment scoping remains. This is what relaxes the
            // stage filter while keeping "only this treatment's items".
            const helperStatusMap = new Map();
            tplData.forEach((t) => {
                helperStatusMap.set(String(t.id), ['CONSULTATION_ATTENDED']);
            });

            const filteredGroupsData = filterGroupsByTreatment(groupsData, {
                tplStatusMap: helperStatusMap,
                tplTreatmentMap,
                leadTreatmentId,
                matchedLeadTreatmentId,
            });

            const flatItems = buildFlatItems(filteredGroupsData);

            setTemplatesEmpty(false);

            // Progress-based all-done detection (BE may omit completed items).
            if (flatItems.length === 0) {
                let progressTotal = 0;
                let progressCompleted = 0;
                // Count progress across ALL treatment-matching groups (no stage filter).
                filteredGroupsData.forEach((group) => {
                    const p = group.progress || {};
                    progressTotal += (p.total || 0);
                    progressCompleted += (p.completed || 0);
                });
                if (progressTotal > 0 && progressCompleted >= progressTotal) {
                    setAllDoneCount(progressTotal);
                    setGroups({ today: [], later: [], done: [] });
                    setUpNext(null);
                    setTemplatePreviewItems([]);
                    return;
                }
            }
            setAllDoneCount(0);

            // Template preview when no spawned tasks match yet.
            if (flatItems.length === 0) {
                const applicable = tplData
                    .filter((t) => {
                        const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                        return items.length > 0 && t.is_active !== false && passesTreatmentFilter(t);
                    })
                    .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));
                const previewItems = [];
                applicable.forEach((tpl) => {
                    const sortedCi = [...(tpl.checklist_items || [])].sort(
                        (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
                    );
                    sortedCi.forEach((ci, idx) => {
                        previewItems.push({
                            id: `preview-${tpl.id}-${idx}`,
                            title: ci.title || ci.name || '',
                            description: ci.description || '',
                            item_type: ci.item_type || ci.action_type || '',
                            due_at: null,
                            completed_at: null,
                            _preview: true,
                            _templateId: tpl.id,
                            _templateName: tpl.name || '',
                        });
                    });
                });
                setTemplatePreviewItems(previewItems);
            } else {
                setTemplatePreviewItems([]);
            }

            const builtGroups = buildGroups(flatItems);
            const nextItem = selectUpNext(flatItems);

            setGroups(builtGroups);
            setUpNext(nextItem);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'completion_checklist_fetch' } });
            setTemplatesEmpty(true);
            setGroups({ today: [], later: [], done: [] });
            setUpNext(null);
            setTemplatePreviewItems([]);
            setAllDoneCount(0);
        } finally {
            hasDataRef.current = true;
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadId, JSON.stringify((leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null })))]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll, templatesUpdatedAt]);

    return {
        loading,
        initialLoading: loading && !hasDataRef.current,
        templatesEmpty,
        groups,
        upNext,
        templatePreviewItems,
        allDoneCount,
        refresh: fetchAll,
    };
}
